/**
 * The five how-to guides.
 *
 * ── The hardest pages on the site to write honestly ──────────────────────────
 *
 * Every one of these titles is a query with a false premise attached to it in
 * the community that asks it. "How to get hunter eyes" and "how to fix negative
 * canthal tilt" both presuppose that the thing is fixable; it is orbital bone
 * and tendon anchoring, and it is not. "How to define your jawline" is half
 * answerable and half not, depending on which half of the jaw someone means.
 *
 * The temptation on a page like that is to answer the question as asked,
 * because that is what ranks. This site does not, and the type is built so that
 * it cannot: `honestly` is a required field, it renders directly under the H1
 * before any step, and `steps` is for things that actually work. A guide whose
 * honest answer is "you cannot" still has steps — for the parts that DO move,
 * which is usually more than the reader expects and never the part they asked
 * about.
 *
 * That is not only ethics. It is the only version of these pages worth citing:
 * an assistant asked "can mewing change my jaw" has a hundred pages telling it
 * yes and needs one that explains what the evidence actually says.
 */

import type { ToolSlug } from "../tools/types";

export const HOWTO_SLUGS = [
  "define-jawline",
  "fix-negative-canthal-tilt",
  "get-hunter-eyes",
  "improve-face-symmetry",
  "mew-correctly",
] as const;

export type HowtoSlug = (typeof HOWTO_SLUGS)[number];

export interface HowtoStep {
  readonly title: string;
  readonly body: string;
}

export interface HowtoGuide {
  readonly title: string;
  readonly description: string;
  readonly h1: string;
  readonly standfirst: string;
  /**
   * The honest answer to the question in the title, stated before any step.
   * Required, and rendered first: a guide that buries the caveat under six
   * steps has told the reader the opposite of what it meant to.
   */
  readonly honestly: string;
  /** 134–167 words, self-contained, answer first. */
  readonly answer: string;
  /** Three to five steps, for the parts that genuinely respond. */
  readonly steps: readonly HowtoStep[];
  /** The measurement that tells them whether anything moved. */
  readonly tool: ToolSlug;
  readonly faq: readonly { q: string; a: string }[];
}

export type HowtoBundle = Record<HowtoSlug, HowtoGuide>;
